import { useLocation } from 'react-router-dom';
import qs from 'qs';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { changeRegisterInputs } from 'modules/serviceSlice';
import { useQuery, useMutation } from 'react-query';
import * as usersAPI from 'lib/api/service/users';
import { validatePassword } from 'utils/validate';

const useRegisterForm = () => {
  const { search } = useLocation();
  const { uuid } = qs.parse(search.slice(1));

  const dispatch = useDispatch();
  const {
    isCheckId,
    password,
    passwordConfirm,
    tel,
    serverAuthCode,
    localAuthCode,
    auth,
  } = useSelector(({ service }) => service.registerInputs);

  const [isNotRegistered, setIsNotRegistered] = useState(null);
  const [localErrors, setLocalErrors] = useState({});
  const [passwordErrors, setPasswordErrors] = useState({});
  const [showNotiModal, setShowNotiModal] = useState(false);
  const [showFailModal, setShowFailModal] = useState(false);
  const [isRegister, setIsRegister] = useState(false);

  const linkData = useQuery('linkData', () => usersAPI.getLinkData(uuid));
  const email = linkData.data?.data.email;

  const checkEmail = useQuery('checkedEmail', () => usersAPI.checkId(email), {
    refetchOnWindowFocus: false,
    enabled: false,
  });

  const sendAuthMsg = useMutation(() => usersAPI.sendAuthMsg(uuid, tel));
  const sendAuthMsgPoC = useMutation(() => usersAPI.sendAuthMsgPoC(uuid, tel));

  const registerFromLink = useMutation(() => {
    if (password) {
      usersAPI.registerFromLink(uuid, {
        user: {
          userId: email,
          password: password,
          tel,
        },
      });
    } else {
      usersAPI.registerFromLink(uuid, {
        user: {
          userId: email,
          tel,
        },
      });
    }
  });

  useEffect(() => {
    checkEmail.data && setIsNotRegistered(checkEmail.data?.data.data);
  }, [checkEmail.data, checkEmail.data?.data.data]);

  useEffect(() => {
    if (sendAuthMsg.isError) {
      dispatch(changeRegisterInputs({ key: 'serverAuthCode', value: '' }));
      setLocalErrors({ ...localErrors, msgSendError: '인증 문자 전송 실패' });
      sendAuthMsg.reset();
    }
    if (sendAuthMsg.isSuccess) {
      dispatch(
        changeRegisterInputs({
          key: 'serverAuthCode',
          value: sendAuthMsg.data.data.code,
        }),
      );
      dispatch(
        changeRegisterInputs({
          key: 'localAuthCode',
          value: '',
        }),
      );
      dispatch(
        changeRegisterInputs({
          key: 'auth',
          value: null,
        }),
      );
      setLocalErrors({ ...localErrors, msgSendError: '' });
      sendAuthMsg.reset();
    }
  }, [localErrors, sendAuthMsg, dispatch]);

  useEffect(() => {
    if (sendAuthMsgPoC.isError) {
      dispatch(changeRegisterInputs({ key: 'serverAuthCode', value: '' }));
      setLocalErrors({ ...localErrors, msgSendError: '인증 문자 전송 실패' });
      sendAuthMsgPoC.reset();
    }
    if (sendAuthMsgPoC.isSuccess) {
      dispatch(
        changeRegisterInputs({
          key: 'serverAuthCode',
          value: sendAuthMsgPoC.data.data.code,
        }),
      );
      dispatch(
        changeRegisterInputs({
          key: 'localAuthCode',
          value: '',
        }),
      );
      dispatch(
        changeRegisterInputs({
          key: 'auth',
          value: null,
        }),
      );
      setLocalErrors({ ...localErrors, msgSendError: '' });
      sendAuthMsgPoC.reset();
    }
  }, [localErrors, sendAuthMsgPoC, dispatch]);

  useEffect(() => {
    if (isRegister && Object.keys(passwordErrors).length === 0) {
      registerFromLink.mutate();
      setIsRegister(false);
    }
  }, [isRegister, passwordErrors, registerFromLink]);

  useEffect(() => {
    if (registerFromLink.isError) {
      setShowFailModal(true);
    }
    if (registerFromLink.isSuccess) {
      setShowNotiModal(true);
    }
  }, [registerFromLink.isError, registerFromLink.isSuccess]);

  const onChange = e => {
    const { name, value } = e.target;
    dispatch(changeRegisterInputs({ key: name, value }));
  };

  const onCheck = () => {
    dispatch(changeRegisterInputs({ key: 'isCheckId', value: true }));
    setLocalErrors({ ...localErrors, isCheckId: '' });
    checkEmail.refetch();
  };

  const onSend = () => sendAuthMsg.mutate();
  const onSendPoC = () => sendAuthMsgPoC.mutate();

  const onCertificate = () => {
    if (localAuthCode.length === 0) {
      return;
    }

    if (serverAuthCode === localAuthCode) {
      dispatch(changeRegisterInputs({ key: 'auth', value: true }));
      return true;
    }

    dispatch(changeRegisterInputs({ key: 'auth', value: false }));
    return false;
  };

  const onRegister = () => {
    if (!isCheckId) {
      setLocalErrors({
        ...localErrors,
        isCheckId: '아이디 중복 조회를 해주세요',
      });
      return;
    }

    if (isNotRegistered) {
      const result = validatePassword(password, passwordConfirm);
      setPasswordErrors({ ...result });
    }

    setIsRegister(true);
  };

  return {
    linkData,
    password,
    passwordConfirm,
    tel,
    serverAuthCode,
    localAuthCode,
    auth,
    isNotRegistered,
    localErrors,
    passwordErrors,
    showNotiModal,
    showFailModal,
    onChange,
    onCheck,
    onSend,
    onSendPoC,
    onCertificate,
    onRegister,
    setShowFailModal,
  };
};

export default useRegisterForm;
